<template>
  <div>
    <v-card>
      <v-tabs v-model="tab">
        <v-tab>
          {{ $t("publicationsAll") }}
        </v-tab>
        <v-tab>
          {{ $t("articles") }}
        </v-tab>
        <v-tab>
          {{ $t("thesis") }}
        </v-tab>
        <v-tab>
          {{ $t("bookAndChapters") }}
        </v-tab>
        <v-tab>
          {{ $t('communications')}}
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item>
          <SkeletonTable :loading="loading" :first-load="firstLoad" :cols="5" :rows="15"/>
          <v-data-table
            :headers="headers"
            :items="publications"
            :search="search"
            :custom-filter="filterItems"
            :items-per-page="15"
            :footer-props="{
              itemsPerPageOptions: [15, 30, -1],
              itemsPerPageText: $t('table.itemsPerPageText')
            }"
            sort-by="title"
            class="table-cursor"
            :loading="loading"
            :loading-text="$t('table.loading')"
            v-show="!firstLoad"
          >
            <template v-slot:top>
              <v-toolbar flat color="white">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="$t('search')"
                  single-line
                  hide-details
                />
                <v-spacer />
              </v-toolbar>
            </template>
            <template v-slot:item.minerva="{ item }">
              <div class="d-flex flex-column align-center justify-center text-center">
                <div class="d-flex align-center">
                  <v-tooltip top v-if="item.minerva_sync_status">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        small
                        v-bind="attrs"
                        v-on="on"
                        :color="minervaStatusColor(item.minerva_sync_status)"
                        :text-color="minervaStatusTextColor(item.minerva_sync_status)"
                      >
                        {{ minervaStatusLabel(item.minerva_sync_status) }}
                      </v-chip>
                    </template>
                    <div class="py-1">
                      <span v-if="minervaStatusDescription(item.minerva_sync_status, item)">
                        {{ minervaStatusDescription(item.minerva_sync_status, item) }}
                      </span>
                      <template v-if="item.minerva_sync_status !== 'wont-sync'">
                        <span v-if="item.minerva_last_checked_at">
                          {{ $t('form.publication.minervaLastCheckedAt') }}:
                          {{ formatMinervaLastCheckedAt(item.minerva_last_checked_at) }}
                        </span>
                        <span v-else>
                          {{ $t('form.publication.minerva.noChecks') }}
                        </span>
                      </template>
                    </div>
                  </v-tooltip>
                  <v-btn
                    v-if="availableMinervaUrl(item)"
                    icon
                    small
                    class="ml-2"
                    :href="availableMinervaUrl(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :aria-label="$t('form.publication.minerva.summary.available')"
                  >
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                </div>
                <span
                  class="caption mt-1"
                v-if="item.minerva_sync_status === 'missing-data' && minervaMetadataSummary(item, { includeMissingDataDetails: false })"
              >
                  {{ minervaMetadataSummary(item, { includeMissingDataDetails: false }) }}
                </span>
              </div>
            </template>
            <template
              v-slot:item.features="{ item }"
            >
              <v-tooltip
                v-if="item.hidden == true"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="red"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-eye-off
                  </v-icon>
                </template>
                {{ $t('table.features.hidden') }}
              </v-tooltip>
              <v-icon
                v-else
                color="grey"
                v-bind="attrs"
                v-on="on"
              >
                mdi-eye-outline
              </v-icon>
              <v-tooltip
                v-if="item.is_external === true"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="pink"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-alien
                  </v-icon>
                </template>
                {{ $t('table.features.isExternal') }}
              </v-tooltip>
              <v-icon
                v-else
                color="grey"
                v-bind="attrs"
                v-on="on"
              >
                mdi-alien-outline
              </v-icon>
              <v-tooltip
                v-if="item.featured === true"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="purple"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-alert-octagram
                  </v-icon>
                </template>
                {{ $t('table.features.isFeatured') }}
              </v-tooltip>
              <v-icon
                v-else
                color="grey"
                v-bind="attrs"
                v-on="on"
              >
                mdi-alert-octagram-outline
              </v-icon>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                text
                class="mr-2"
                color="primary"
                :to="`/manage/edit/publications/edit/${item.id}`"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-icon color="red" @click="deleteItem(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-tab-item>
        <v-tab-item>
          <EditArticleList />
        </v-tab-item>
        <v-tab-item>
          <EditThesisList />
        </v-tab-item>
        <v-tab-item>
          <EditBookAndChapterList />
        </v-tab-item>
        <v-tab-item>
          <EditCommunicationList />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import EditArticleList from "./EditArticleList";
import EditThesisList from "./EditThesisList";
import EditBookAndChapterList from "./EditBookAndChapterList";
import EditCommunicationList from "./EditCommunicationList";
import SkeletonTable from "../../../components/reusables/SkeletonTable";
import minervaDisplay from "../../../mixins/minervaDisplay";
export default {
  mixins: [minervaDisplay],
  components: { EditArticleList, EditThesisList, EditBookAndChapterList,EditCommunicationList, SkeletonTable },
  data() {
    return {
      tab: null,
      loading: true,
      firstLoad: true,
      search: "",
      publications: []
    };
  },

  computed: {
    headers() {
      return [
        {
          text: this.$t("title.publication"),
          value: "title",
          width: "31%"
        },
        { text: this.$t("form.authors"), 
          value: "authors_text",
          width: "27%"
        },
        {
          text: this.$t("form.features"),
          value: "features",
          filterable: false,
          width: "15%",
          sortable: false
        },
        {
          text: this.$t("table.minervaSync"),
          value: "minerva",
          sortable: false,
          width: "12%"
        },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "end",
          width: "15%",
          filterable: false,
          sortable: false
        }
      ];
    }
  },
  created() {
    this.initialize();
  },

  methods: {
    initialize() {
      let path = this.$store.state.endpoint + "/publication";
      axios
        .get(path)
        .then(response => {
          this.publications = response.data;
          this.loading = false;
          if (this.firstLoad) this.firstLoad = false;
        })
        .catch(error => {
          console.log(error);
        });
    },
    deleteItem(item) {
      if (confirm(this.$t("message.confirm.delete"))) {
        let path = this.$store.state.endpoint + "/publication/" + item.id;
        axios
          .delete(path)
          .then(response => {
            const index = this.publications.indexOf(item);
            this.publications.splice(index, 1);
            this.$store.commit("alert", {
              color: "success",
              text: this.$t("message.correct.deleteItem")
            });
          })
          .catch(error => {
            console.log(error);
            this.$store.commit("alert", {
              color: "error",
              text: error.response
            });
          });
      }
    },
    filterItems(value, search, item) {
      const search_array = search
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .split(",")
        .map(x => x.trim());
      const today = new Date().toISOString().split('T')[0];
      const thisyear = today.split('-')[0];
      const customValue = (
        item.title +
        " " +
        item.authors_text +
        " " +
        ((item.doi !== null && item.doi.trim() !== "") ? ("doi:" + item.doi) : "no-doi") + 
        (item.is_external !== false ? " external" : "") +
        (item.hidden ? " hidden" : "") +
        " " + (item.language || "") +
        " " + (item.minerva_sync_option || "") +
        " " + (item.minerva_sync_status || "no-minerva") +
        " " + JSON.stringify(item.minerva_sync_metadata || {}) +
        " " + (item.minerva_last_checked_at || "")
      )
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return (
        customValue != null &&
        search_array != null &&
        typeof customValue === "string" &&
        search_array.every(function(search){ return customValue.indexOf(search) !== -1 })
      );
    },
  }
};
</script>
